import type { ReplyData } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { ViewValues } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
export const DEFAULT_NUM_VISIBLE_REPLIES = 2;

export enum ViewReplyOptions {
	ALL = 'ALL', // shows all replies
	DEFAULT = 'DEFAULT', // shows up to 3 unread comments
	HIDDEN = 'HIDDEN',
	LIMITED = 'LIMITED',
}

export type ComputeVisibleRepliesProps = {
	currentReplyView: ViewReplyOptions;
	allReplies: ReplyData[];
	isParentCommentOpen: boolean;
	latestReplies: ReplyData[];
	parentCommentId: string;
	currentView?: ViewValues;
	allowNestedReplies?: boolean;
};

// Derives the visible replies for a comment thread from props/state. Pure and
// SSR-safe — see useVisibleReplies for the hook wrapper.
export const computeVisibleReplies = ({
	currentReplyView,
	allReplies,
	isParentCommentOpen,
	latestReplies,
	parentCommentId,
	currentView,
	allowNestedReplies = false,
}: ComputeVisibleRepliesProps): ReplyData[] => {
	if (!isParentCommentOpen && currentView !== ViewValues.RESOLVED) {
		return [];
	}

	switch (currentReplyView) {
		case ViewReplyOptions.ALL:
			return allowNestedReplies
				? allReplies
				: // Make all direct replies to the parent comment visible, excluding any nested replies.
					getCommentReplies(allReplies, parentCommentId);
		case ViewReplyOptions.HIDDEN:
			return [];
		case ViewReplyOptions.LIMITED:
			return latestReplies;
		default:
			return [];
	}
};

export const getCommentReplies = (replies: ReplyData[], parentCommentId: string): ReplyData[] =>
	replies.filter(({ parentId }) => parentId === parentCommentId);

type GetNumOfHiddenRepliesProps = {
	replies: ReplyData[];
	latestReplyIds: Set<string>;
	currentReplyView: ViewReplyOptions;
};

export const getNumOfHiddenReplies = ({
	replies,
	latestReplyIds,
	currentReplyView,
}: GetNumOfHiddenRepliesProps): number => {
	switch (currentReplyView) {
		case ViewReplyOptions.HIDDEN:
			return replies.length;
		case ViewReplyOptions.LIMITED:
			return replies.length - latestReplyIds.size;
		default:
			return 0;
	}
};

export const getDefaultHiddenReplies = ({
	replies,
	latestReplyIds,
}: {
	replies: ReplyData[];
	latestReplyIds: Set<string>;
}): ReplyData[] =>
	replies.filter((reply) => {
		return !latestReplyIds.has(reply.id);
	});

export type ProfilePictureInfo = {
	displayName: string;
	profilePicturePath: string | undefined;
};

// filter out duplicate authors when we're showing the profile pictures
export const getProfilePicturesOfHiddenReplies = (
	repliesToFilter: ReplyData[],
): ProfilePictureInfo[] => {
	const uniqueAccountsMap = new Map<string, ProfilePictureInfo>();
	// Start from the end of the list and move backwards
	for (let i = repliesToFilter.length - 1; i >= 0; i--) {
		const reply = repliesToFilter[i];
		const { displayName, profilePicture } = reply.author;
		// Determine if the author has an accountId, anonymous users don't have accountId
		let uniqueKey: string;
		if ('accountId' in reply.author && reply.author.accountId) {
			uniqueKey = reply.author.accountId;
		} else {
			// Fallback for anonymous users or if accountId is not present
			uniqueKey = `anonymous-${i}`;
		}
		if (!uniqueAccountsMap.has(uniqueKey)) {
			uniqueAccountsMap.set(uniqueKey, {
				displayName: displayName ?? '',
				profilePicturePath: profilePicture?.path,
			});
		}
	}
	return Array.from(uniqueAccountsMap.values());
};

type GetShouldShowRepliesSectionProps = {
	replies?: ReplyData[];
	isEditorExpanded: boolean;
};

export const getShouldShowRepliesSection = ({
	replies = [],
	isEditorExpanded,
}: GetShouldShowRepliesSectionProps): boolean => replies.length >= 1 || isEditorExpanded;
